import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../images/olympiq-logo.svg';
import { WHATSAPP_URL, INSTAGRAM_URL } from '../../utils/siteLinks';

interface MobileMenuProps {
  onClose: () => void;
}

interface MobileNavCard {
  to: string;
  icon: string;
  label: string;
  hint: string;
  delay: string;
}

/** Nav cards with the mockup's staggered menuItem delays (L163–187). */
const NAV_CARDS: MobileNavCard[] = [
  { to: '/', icon: 'mdi-view-dashboard-outline', label: 'Product', hint: 'Features & the app', delay: '.05s' },
  { to: '/compare', icon: 'mdi-scale-balance', label: 'Compare', hint: 'vs Spond, WebCollect & more', delay: '.11s' },
  { to: '/help-centre', icon: 'mdi-lifebuoy', label: 'Help', hint: 'Guides & the AI assistant', delay: '.17s' },
  { to: '/contact-us', icon: 'mdi-email-outline', label: 'Contact', hint: 'Talk to the team', delay: '.23s' },
];

/** Full-screen mobile nav overlay — source: mockup L148–200 (without the Log in button). */
const MobileMenu = ({ onClose }: MobileMenuProps): React.JSX.Element => {
  const { pathname } = useLocation();
  const openedAt = useRef(pathname);

  // Lock the page behind the overlay, and always hand scrolling back. Both
  // elements are locked, as the mockup does (L1097–1098) — body alone leaks
  // scroll to the root element on iOS Safari.
  useEffect(() => {
    const { body, documentElement } = document;
    const previousBody = body.style.overflow;
    const previousRoot = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousBody;
      documentElement.style.overflow = previousRoot;
    };
  }, []);

  useEffect(() => {
    if (pathname !== openedAt.current) onClose();
  }, [pathname, onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="oq-mobile-menu" role="dialog" aria-modal="true" aria-label="Menu">
      <span className="oq-mobile-menu__glow" aria-hidden="true" />

      <div className="oq-mobile-menu__top">
        <div className="oq-mobile-menu__brand">
          <img src={logo} alt="Olympiq" width={36} height={36} className="oq-mobile-menu__logo" />
          <span className="oq-bebas oq-mobile-menu__wordmark">OLYMPIQ</span>
        </div>
        <button type="button" aria-label="Close menu" className="oq-mobile-menu__close" onClick={onClose}>
          <i className="mdi mdi-close" aria-hidden="true" />
        </button>
      </div>

      <nav className="oq-mobile-menu__nav">
        {NAV_CARDS.map((card) => (
          <Link
            key={card.label}
            to={card.to}
            className="oq-mobile-menu__card"
            style={{ animationDelay: card.delay }}
            onClick={onClose}
          >
            <span className="oq-mobile-menu__card-icon">
              <i className={`mdi ${card.icon}`} aria-hidden="true" />
            </span>
            <span className="oq-mobile-menu__card-text">
              <span className="oq-bebas oq-mobile-menu__card-label">{card.label}</span>
              <span className="oq-mobile-menu__card-hint">{card.hint}</span>
            </span>
            <i className="mdi mdi-chevron-right oq-mobile-menu__card-chevron" aria-hidden="true" />
          </Link>
        ))}
      </nav>

      <div className="oq-mobile-menu__footer">
        <Link to="/contact-us" className="oq-mobile-menu__cta" onClick={onClose}>
          Book a demo
        </Link>
        <div className="oq-mobile-menu__socials">
          <a
            href={WHATSAPP_URL}
            aria-label="WhatsApp"
            className="oq-mobile-menu__social"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="mdi mdi-whatsapp" aria-hidden="true" />
          </a>
          <a
            href={INSTAGRAM_URL}
            aria-label="Instagram"
            className="oq-mobile-menu__social"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="mdi mdi-instagram" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
